import { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useIntl } from "react-intl";

import { Box, ButtonBase, Typography } from "@mui/material";

import { ProgressOverlay } from "@framework/mui-page-layout";
import { useUser } from "@framework/provider-user";
import type { IUser } from "@framework/types";

import { useSpeed } from "../../../../../../context/speed-context";
import { createStableColorAssigner } from "../../../scheme/components/analytics/utils/line-colors";
import { formatMeasurementNumber } from "../../../scheme/utils/measurement-format";
import type { ISpeedScreenFormContentProps } from "../types";
import { NyquistChart } from "./charts";
import {
  buildNyquistSeries,
  formatNyquistHeader,
  formatNyquistValues,
  formatNyquistUnitLabel,
  getNyquistFrameMarks,
  groupNyquistSeriesByUnits,
  PHASE_UNIT_CODE,
  resolveNyquistCursor,
} from "./model";

export const NyquistScreenForm: FC<ISpeedScreenFormContentProps> = ({ windowId }) => {
  const { formatMessage } = useIntl();
  const { profile } = useUser<IUser>();
  const { selectedPoints, dataMarks, selectedDataMarks, frameRange, dataMarksError, isDataMarksLoading, toolbarState } =
    useSpeed();
  const colors = useRef(createStableColorAssigner());
  const [cursorKey, setCursorKey] = useState<string | null>(null);
  const [hiddenSeriesKeys, setHiddenSeriesKeys] = useState<string[]>([]);
  const hiddenSeries = useMemo(() => new Set(hiddenSeriesKeys), [hiddenSeriesKeys]);
  const toggleSeries = useCallback((key: string) => {
    setHiddenSeriesKeys(keys => (keys.includes(key) ? keys.filter(item => item !== key) : [...keys, key]));
  }, []);
  const marks = useMemo(() => getNyquistFrameMarks(dataMarks, frameRange), [dataMarks, frameRange]);
  const series = useMemo(
    () =>
      buildNyquistSeries(
        marks,
        unitCode => formatNyquistUnitLabel(formatMessage({ id: `enums.units.${unitCode}` })),
        key => colors.current.get(key),
      ),
    [formatMessage, marks],
  );
  const polarGroups = useMemo(() => groupNyquistSeriesByUnits(series), [series]);
  const selectedMarkKey = selectedDataMarks[selectedDataMarks.length - 1]?.key ?? null;
  useEffect(() => {
    if (selectedMarkKey) setCursorKey(selectedMarkKey);
  }, [selectedMarkKey]);
  const visibleSampleKeys = useMemo(
    () =>
      new Set(
        series.filter(item => !hiddenSeries.has(item.key)).flatMap(item => item.samples.map(sample => sample.key)),
      ),
    [hiddenSeries, series],
  );
  const cursor = resolveNyquistCursor(
    marks.filter(mark => visibleSampleKeys.has(mark.key)),
    cursorKey,
  );
  const formatValue = useCallback(
    (value: number, unitCode: number | null) =>
      formatMeasurementNumber(value, unitCode, profile.interfaceSettings.dataFormat, { fallbackDigits: 2 }),
    [profile.interfaceSettings.dataFormat],
  );
  const header = cursor
    ? formatNyquistHeader(
        cursor,
        formatNyquistValues(
          cursor,
          formatNyquistUnitLabel(formatMessage({ id: `enums.units.${cursor.units}` })),
          formatNyquistUnitLabel(formatMessage({ id: `enums.units.${PHASE_UNIT_CODE}` })),
          formatValue,
        ),
        formatValue,
      )
    : null;
  const placeholder = !selectedPoints.length
    ? "pages.multiAnalysis.chartPlaceholder"
    : dataMarksError
      ? "pages.speed.nyquist.loadError"
      : "pages.multiAnalysis.chartEmpty";

  return (
    <Box
      data-testid={`speed-nyquist-${windowId}`}
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        flex: 1,
        minWidth: 0,
        minHeight: 0,
        height: "100%",
      }}
    >
      {isDataMarksLoading ? <ProgressOverlay isLoading /> : null}
      {series.length ? (
        <>
          <Typography
            title={header ?? undefined}
            sx={{
              px: 1,
              pt: 1,
              pb: 0.5,
              fontSize: 14,
              fontWeight: 500,
              color: "text.primary",
              overflowWrap: "anywhere",
              flexShrink: 0,
            }}
            aria-live="polite"
          >
            {header}
          </Typography>
          <Typography sx={{ px: 1, pb: 1, fontSize: 12, color: "text.secondary", flexShrink: 0 }}>
            {formatMessage({ id: "pages.speed.nyquist.chartKeyboardHint" })}
          </Typography>
          <Box sx={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "auto" }}>
            <Box sx={{ px: 1, pt: 0.5, display: "flex", gap: 1.5, flexWrap: "wrap", flexShrink: 0 }}>
              {toolbarState.showNyquistAmplitudePhase
                ? ["amplitude", "phase"].map(label => (
                    <Box key={label} sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      <Box
                        sx={{
                          width: 18,
                          borderTop: "2px",
                          borderTopStyle: label === "phase" ? "dashed" : "solid",
                          borderTopColor: "text.secondary",
                        }}
                      />
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {formatMessage({ id: `pages.speed.nyquist.${label}` })}
                      </Typography>
                    </Box>
                  ))
                : null}
              {series.map(item => (
                <ButtonBase
                  key={item.key}
                  aria-pressed={!hiddenSeries.has(item.key)}
                  onClick={() => toggleSeries(item.key)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    minWidth: 0,
                    borderRadius: 0.5,
                    px: 0.5,
                    py: 0.25,
                    opacity: hiddenSeries.has(item.key) ? 0.5 : 1,
                    "&.Mui-focusVisible": { outline: "2px solid", outlineColor: "primary.main", outlineOffset: 1 },
                  }}
                >
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 }} />
                  <Typography
                    sx={{
                      fontSize: 12,
                      color: "text.secondary",
                      overflowWrap: "anywhere",
                      textDecoration: hiddenSeries.has(item.key) ? "line-through" : "none",
                    }}
                  >
                    {item.name}
                  </Typography>
                </ButtonBase>
              ))}
            </Box>
            {toolbarState.showNyquistAmplitudePhase ? (
              <Box
                sx={{
                  flex: "1 0 160px",
                  minHeight: 160,
                  display: "flex",
                  flexDirection: "column",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <NyquistChart
                  kind="cartesian"
                  series={series}
                  cursorKey={cursor?.key ?? null}
                  onCursorChange={setCursorKey}
                  formatValue={formatValue}
                  hiddenSeriesKeys={hiddenSeriesKeys}
                />
              </Box>
            ) : null}
            {polarGroups.map(group => (
              <Box
                key={group[0].unitCode}
                sx={{ flex: "2 0 220px", minHeight: 220, display: "flex", flexDirection: "column" }}
              >
                <NyquistChart
                  kind="polar"
                  series={group}
                  cursorKey={cursor?.key ?? null}
                  onCursorChange={setCursorKey}
                  formatValue={formatValue}
                  rotationDirection={toolbarState.rotationDirection}
                  showRotationFrequency={toolbarState.showNyquistRotationFrequency}
                  hiddenSeriesKeys={hiddenSeriesKeys}
                />
              </Box>
            ))}
          </Box>
        </>
      ) : (
        <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
          {!isDataMarksLoading ? (
            <Typography color="textSecondary" sx={{ fontSize: 13 }} align="center">
              {formatMessage({ id: placeholder })}
            </Typography>
          ) : null}
        </Box>
      )}
    </Box>
  );
};
